// ─────────────────────────────────────────────────────────
// Einstellungen → Mein Konto → Passwort ändern
// ─────────────────────────────────────────────────────────
// Der erste Bereich, der NICHT der Firma gehört, sondern dem angemeldeten
// Nutzer — darum eigene Gruppe in der Settings-Navigation und KEIN
// Permission-Gate: sein eigenes Passwort darf jeder ändern, unabhängig von
// der Rolle. Prefix `ac-`.
//
// Die Regeln (Server: auth.service.ts changePassword):
//  · das alte Passwort ist Pflicht — ein offener Browser ist kein Beweis,
//    dass der Konto-Inhaber davor sitzt;
//  · danach sind ALLE anderen Geräte abgemeldet. Das steht auch so im UI,
//    denn es ist eine Folge, die man vorher wissen will;
//  · dieses Gerät bleibt angemeldet (der Server gibt eine frische Session).
import { icon, escapeHtml } from '../../utils/dom.js';
import { changePassword, getMfaStatus, startMfaSetup, enableMfa, disableMfa } from '../../core/auth.js';
import { devicesCard } from './settingsDevices.js';
import { t } from '../../i18n/index.js';

export function renderAccountSection(ctx) {
  const { s } = ctx;
  const dis = s.acSaving ? 'disabled' : '';
  const err = s.acError
    ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(s.acError)}</span></div>`
    : '';
  const done = s.acDone
    ? `<div class="cd-emp-info">${icon('check_circle', 18)}<span>${escapeHtml(t('settings.account.done'))}</span></div>`
    : '';

  return `
    <div class="cd-emp">
      ${err}${done}
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('password', 18)}<h3>${escapeHtml(t('settings.account.pwTitle'))}</h3></div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.account.pwLead'))}</p>

        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="ac-cur">${escapeHtml(t('settings.account.current'))}</label>
          <input class="cd-input" id="ac-cur" name="ac-cur" type="password" autocomplete="current-password" maxlength="128" ${dis}>
        </div>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="ac-new">${escapeHtml(t('settings.account.new'))}</label>
          <input class="cd-input" id="ac-new" name="ac-new" type="password" autocomplete="new-password" maxlength="128" ${dis}>
        </div>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="ac-new2">${escapeHtml(t('settings.account.repeat'))}</label>
          <input class="cd-input" id="ac-new2" name="ac-new2" type="password" autocomplete="new-password" maxlength="128" ${dis}>
        </div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.account.rule'))}</p>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('devices', 18)}<span>${escapeHtml(t('settings.account.logoutNote'))}</span></div>
      ${mfaCard(ctx)}
      ${devicesCard(ctx)}
    </div>`;
}

export function accountHeadButton(ctx) {
  const saving = ctx.s.acSaving;
  return `<button class="cd-btn-primary" data-action="ac-save" ${saving ? 'disabled' : ''}>${escapeHtml(saving ? t('common.saving') : t('settings.account.save'))}</button>`;
}

const FIELDS = ['ac-cur', 'ac-new', 'ac-new2'];

// draw() ersetzt den Bereich per innerHTML — die getippten Passwörter wären
// danach weg. Bei einem Tippfehler müsste man ALLE DREI neu eingeben, genau
// an der Stelle, an der man ohnehin schon kämpft (dieselbe Lektion wie
// redrawKeepingInput in auth-gate/authShell.js). Darum: Werte merken, neu
// zeichnen, zurückschreiben. BEWUSST nur in einer lokalen Variablen und nicht
// im App-State `s` — Klartext-Passwörter gehören nicht in den Zustand.
function redrawKeepingPasswords(draw, values) {
  draw();
  FIELDS.forEach((id, i) => {
    const el = document.getElementById(id);
    if (el) el.value = values[i];
  });
}

export function handleAccountAction(a, el, ctx) {
  const { s, draw } = ctx;
  if (a !== 'ac-save') return false;

  const vals = FIELDS.map((id) => document.getElementById(id)?.value || '');
  const [cur, nw, nw2] = vals;

  // Wiederholung + Länge HIER prüfen: ein Tippfehler im zweiten Feld darf
  // nicht dazu führen, dass ein falsches Passwort gesetzt wird und der Nutzer
  // von allen Geräten fliegt.
  s.acDone = false;
  const fail = (key) => { s.acError = t(key); redrawKeepingPasswords(draw, vals); return true; };
  if (!cur || !nw) return fail('settings.account.errEmpty');
  if (nw !== nw2) return fail('settings.account.errMismatch');
  if (nw.trim().length < 8) return fail('settings.account.errShort');

  s.acError = '';
  s.acSaving = true;
  redrawKeepingPasswords(draw, vals); // Knopf auf «Speichern…», Eingaben bleiben
  changePassword(cur, nw)
    .then(() => { s.acDone = true; })
    .catch((e) => { s.acError = e?.message || t('common.saveFailed'); })
    .finally(() => {
      s.acSaving = false;
      // Erfolg → leer (das Passwort ist gesetzt, es soll nicht im DOM stehen
      // bleiben); Fehler → Eingaben behalten, damit man nur korrigieren muss.
      if (s.acDone) draw();
      else redrawKeepingPasswords(draw, vals);
    });
  return true;
}


// ─────────────────────────────────────────────────────────
// Zwei-Faktor-Authentifizierung (TOTP) — CASA 3.3.1
// ─────────────────────────────────────────────────────────
// Vier Zustände, die sich gegenseitig ausschliessen, deshalb eine einzige
// Karte mit klaren Zweigen statt verschachtelter Bedingungen:
//   aus · Einrichtung läuft (QR + erster Code) · Backup-Codes anzeigen · an.
//
// Der Status kommt vom Server (`s.mfa`), nicht aus dem lokalen Zustand: ob MFA
// aktiv ist, entscheidet die Datenbank — sonst zeigt ein zweiter Tab Unsinn an.
// `required` (Plattform-Konten) blendet den Abschalten-Weg aus: was der Prüfer
// verlangt, darf die Oberfläche nicht wegklickbar machen.
// Exportiert, weil Plattform-Konten das Dashboard nie sehen: sie bekommen
// dieselbe Karte im MfaSetupDialog (components/mfa/) — eine Karte, zwei Orte.
export function mfaCard(ctx) {
  const { s } = ctx;
  const m = s.mfa;
  const dis = s.mfaBusy ? 'disabled' : '';
  const err = s.mfaError
    ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(s.mfaError)}</span></div>`
    : '';

  // Status noch nicht geladen → einmalig anfordern, solange nichts zeigen.
  if (!m) {
    if (!s.mfaLoading) {
      s.mfaLoading = true;
      getMfaStatus()
        .then((res) => { s.mfa = res; })
        .catch((e) => { s.mfaError = e?.message || ''; })
        .finally(() => { s.mfaLoading = false; ctx.draw(); });
    }
    return '';
  }

  const head = `<div class="cd-emp-card-head">${icon('encrypted', 18)}<h3>${escapeHtml(t('settings.account.mfaTitle'))}</h3></div>`;

  // ── Backup-Codes: erscheinen genau EINMAL nach der Aktivierung ──
  if (s.mfaBackupCodes?.length) {
    return `
      <div class="cd-emp-card">
        ${head}
        <div class="cd-emp-info">${icon('check_circle', 18)}<span>${escapeHtml(t('settings.account.mfaEnabledNow'))}</span></div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.account.mfaBackupLead'))}</p>
        <pre class="ac-mfa-codes">${escapeHtml(s.mfaBackupCodes.join('\n'))}</pre>
        <button class="cd-btn-primary" data-action="ac-mfa-codes-done">${escapeHtml(t('settings.account.mfaBackupDone'))}</button>
      </div>`;
  }

  // ── Einrichtung läuft: QR + erster Code ──
  if (s.mfaSetup) {
    return `
      <div class="cd-emp-card">
        ${head}
        ${err}
        <p class="cd-sc-lead">${escapeHtml(t('settings.account.mfaScanLead'))}</p>
        <img class="ac-mfa-qr" src="${escapeHtml(s.mfaSetup.qrDataUrl)}" alt="QR" width="180" height="180">
        <p class="cd-sc-lead">${escapeHtml(t('settings.account.mfaManualKey'))}</p>
        <pre class="ac-mfa-codes">${escapeHtml(s.mfaSetup.secret)}</pre>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="ac-mfa-code">${escapeHtml(t('settings.account.mfaCodeLabel'))}</label>
          <input class="cd-input" id="ac-mfa-code" name="ac-mfa-code" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="20" ${dis}>
        </div>
        <button class="cd-btn-primary" data-action="ac-mfa-enable" ${dis}>${escapeHtml(s.mfaBusy ? t('common.saving') : t('settings.account.mfaActivate'))}</button>
        <button class="cd-btn-ghost" data-action="ac-mfa-cancel" ${dis}>${escapeHtml(t('common.cancel'))}</button>
      </div>`;
  }

  // ── Aktiv ──
  if (m.enabled) {
    return `
      <div class="cd-emp-card">
        ${head}
        ${err}
        <div class="cd-emp-info">${icon('verified_user', 18)}<span>${escapeHtml(t('settings.account.mfaOn'))}</span></div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.account.mfaCodesLeft').replace('{n}', String(m.remainingBackupCodes)))}</p>
        ${m.required
          ? `<div class="cd-emp-info cd-sc-note">${icon('policy', 18)}<span>${escapeHtml(t('settings.account.mfaRequired'))}</span></div>`
          : `<div class="cd-dt-field">
               <label class="cd-dt-cap" for="ac-mfa-pw">${escapeHtml(t('settings.account.current'))}</label>
               <input class="cd-input" id="ac-mfa-pw" name="ac-mfa-pw" type="password" autocomplete="current-password" maxlength="128" ${dis}>
             </div>
             <div class="cd-dt-field">
               <label class="cd-dt-cap" for="ac-mfa-code">${escapeHtml(t('settings.account.mfaCodeLabel'))}</label>
               <input class="cd-input" id="ac-mfa-code" name="ac-mfa-code" type="text" inputmode="numeric" maxlength="20" ${dis}>
             </div>
             <button class="cd-btn-ghost" data-action="ac-mfa-disable" ${dis}>${escapeHtml(t('settings.account.mfaDeactivate'))}</button>`}
      </div>`;
  }

  // ── Aus ──
  return `
    <div class="cd-emp-card">
      ${head}
      ${err}
      <p class="cd-sc-lead">${escapeHtml(t('settings.account.mfaLead'))}</p>
      ${m.required ? `<div class="cd-emp-info cd-sc-note">${icon('policy', 18)}<span>${escapeHtml(t('settings.account.mfaRequiredSetup'))}</span></div>` : ''}
      <button class="cd-btn-primary" data-action="ac-mfa-setup" ${dis}>${escapeHtml(s.mfaBusy ? t('common.saving') : t('settings.account.mfaSetup'))}</button>
    </div>`;
}

export function handleMfaAction(a, el, ctx) {
  const { s, draw } = ctx;
  const done = () => { s.mfaBusy = false; draw(); };

  if (a === 'ac-mfa-setup') {
    s.mfaBusy = true; s.mfaError = ''; draw();
    startMfaSetup()
      .then((res) => { s.mfaSetup = res; })
      .catch((e) => { s.mfaError = e?.message || t('common.saveFailed'); })
      .finally(done);
    return true;
  }

  if (a === 'ac-mfa-cancel') {
    // Nur die Oberfläche zurücksetzen: das halbe Secret in der DB stört nicht,
    // ein neuer Setup-Lauf überschreibt es, und aktiv ist ohnehin nichts.
    s.mfaSetup = null; s.mfaError = ''; draw();
    return true;
  }

  if (a === 'ac-mfa-enable') {
    const code = document.getElementById('ac-mfa-code')?.value?.trim() || '';
    if (!code) { s.mfaError = t('settings.account.mfaCodeRequired'); draw(); return true; }
    s.mfaBusy = true; s.mfaError = ''; draw();
    enableMfa(code)
      .then((res) => {
        s.mfaBackupCodes = res.backupCodes;
        s.mfaSetup = null;
        s.mfa = null; // Status neu vom Server holen
      })
      .catch((e) => { s.mfaError = e?.message || t('settings.account.mfaCodeInvalid'); })
      .finally(done);
    return true;
  }

  if (a === 'ac-mfa-codes-done') {
    s.mfaBackupCodes = null; draw();
    return true;
  }

  if (a === 'ac-mfa-disable') {
    const pw = document.getElementById('ac-mfa-pw')?.value || '';
    const code = document.getElementById('ac-mfa-code')?.value?.trim() || '';
    if (!pw || !code) { s.mfaError = t('settings.account.mfaDisableFields'); draw(); return true; }
    s.mfaBusy = true; s.mfaError = ''; draw();
    disableMfa(pw, code)
      .then(() => { s.mfa = null; })
      .catch((e) => { s.mfaError = e?.message || t('common.saveFailed'); })
      .finally(done);
    return true;
  }

  return false;
}
